import React, {useEffect, useRef } from 'react';
import { Map, TileLayer, FeatureGroup, Polygon, Tooltip, ZoomControl,Marker,Popup } from 'react-leaflet';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import _ from 'lodash';
import DeviceMapWrapper from './DeviceMap.styles';
import DeviceMapMarker from './DeviceMapMarker';


const DEFAULT_CENTER = [-25.3444, 131.0369];
export const DEFAULT_ZOOM = 16;

// Accepts both shapes we get fed: a plain [lat, lng] array and a Leaflet LatLng.
const toLatLng = (value) => {
  if (!value) {
    return null;
  }
  const lat = Array.isArray(value) ? value[0] : value.lat;
  const lng = Array.isArray(value) ? value[1] : value.lng;
  return (lat != null && lng != null) ? [lat, lng] : null;
};

const DeviceMap = (props) => {
  const {centerPoint, getPositionDevice, positionDevice, propertyId, loading, setLoading, showMarkerAlert, setShowMarkerAlert, mapRef, propertyPosition, primaryObjects, hasFittedDevicesRef } = props;

  // `center`/`zoom` seed the initial view only — they are never updated again.
  // react-leaflet re-issues setView(center, zoom) every time the controlled
  // center prop changes, so a slow response resolving after the device
  // fitBounds would otherwise throw the fitted viewport away. Everything after
  // mount is driven imperatively through mapRef instead.
  const initialView = useRef(null);
  if (initialView.current === null) {
    initialView.current = toLatLng(centerPoint) || toLatLng(propertyPosition) || DEFAULT_CENTER;
  }

  const getMap = () => mapRef.current && mapRef.current.leafletElement;

  // Pan to a device picked from the alert history.
  useEffect(() => {
    const map = getMap();
    const point = toLatLng(centerPoint);
    if (map && point) {
      map.setView(point, DEFAULT_ZOOM);
    }
  }, [centerPoint]);

  // Fallback view for the property itself — only used until the devices have
  // been fitted. The ref is re-checked here (and not just where the state is
  // set) because this effect flushes after the render that queued it, and the
  // device response can land in between.
  useEffect(() => {
    const map = getMap();
    const point = toLatLng(propertyPosition);
    if (map && point && !(hasFittedDevicesRef && hasFittedDevicesRef.current)) {
      map.setView(point, DEFAULT_ZOOM);
    }
  }, [propertyPosition]);

  // Render primary objects (polygons) on the map
  const renderPrimaryObjects = () => {
    if (!primaryObjects || primaryObjects.length === 0) {
      return null;
    }

    const colorMap = {
      'blue': '#31a6c5',
      'green': '#52c41a',
      'red': '#f5222d',
      'yellow': '#faad14',
      'purple': '#722ed1',
      'orange': '#fa8c16',
    };

    return primaryObjects.map((object) => {
      const color = colorMap[object.color] || '#31a6c5';

      return (
        <Polygon
          key={object.id}
          positions={object.area}
          fillColor={color}
          color={color}
          fillOpacity={0.2}
          weight={2}
        >
          <Tooltip permanent={true} direction="center" className="primary-objects-tooltip">
            <div>
              <p style={{ margin: 0, fontWeight: 'bold' }}>{object.name}</p>
              <p style={{ margin: 0, fontSize: '11px' }}>
                {parseFloat(object.acreage).toFixed(1)} <span>{object.acreage_unit}</span>
              </p>
            </div>
          </Tooltip>
        </Polygon>
      );
    });
  };

  return (
    <DeviceMapWrapper>
      <Map
        ref={mapRef}
        center={initialView.current}
        doubleClickZoom={false}
        zoom={DEFAULT_ZOOM}
        zoomControl={true}
        style={{width:'100%', height:400}}>
        <ReactLeafletGoogleLayer type={'hybrid'} useGoogMapsLoader={false}/>
        <FeatureGroup>
          {renderPrimaryObjects()}
        </FeatureGroup>
        <DeviceMapMarker
          propertyId={propertyId}
          getPositionDevice={getPositionDevice}
          setLoading={setLoading}
          loading={loading}
          positionDevice={positionDevice}
          showMarkerAlert={showMarkerAlert}
          setShowMarkerAlert={setShowMarkerAlert}
        />
      </Map>
    </DeviceMapWrapper>
  );
}

export default DeviceMap;

